import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Messages from "../../../constants/Messages";
import uRLs from "../../../utils/baseUtils";
import { useShowAlert } from "../../../context/AlertContext";
import { useAuth } from "../../../context/AuthProvider";
import { useNetwork } from "../../../context/NetworkContext";
import { CheckIcon, ChevronLeftIcon } from "../../../constants/Icons";

const AuthStore = () => {
    const { handleLogin } = useAuth();
    const { checkNetwork } = useNetwork();
    const showAlert = useShowAlert();
    const navigate = useNavigate();
    const location = useLocation();

    const accessToken = location.state?.accessToken;
    const successRedirect = location.state?.successRedirect;
    const failureRedirect = location.state?.failureRedirect;

    const titleInputRef = useRef(null);
    const phoneInputRef = useRef(null);
    const verifyCodeInputRef = useRef(null);

    const [isStoreData, setStoreData] = useState({ title: "", phone: "", verifycode: "" });

    const [isBtnLoading, setBtnLoading] = useState(false);
    const [isVerifyLoading, setVerifyLoading] = useState(false);
    const [isCreateLoading, setCreateLoading] = useState(false);
    const [isResendBtnLoading, setResendBtnLoading] = useState(false);

    const [error, setErrors] = useState({});
    const [errorMsg, setErrorMsg] = useState(null);
    const [isPhoneVerified, setPhoneVerified] = useState(false);
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    useEffect(() => {
        if (currentStep === 1) {
            titleInputRef.current?.focus();
        } else if (currentStep === 2 && !showOtpInput) {
            phoneInputRef.current?.focus();
        } else if (currentStep === 2 && showOtpInput && !isPhoneVerified) {
            verifyCodeInputRef.current?.focus();
        }
    }, [currentStep, showOtpInput, isPhoneVerified]);

    useEffect(() => {
        if (!accessToken || !successRedirect || !failureRedirect) {
            navigate(`/${failureRedirect}`);
        }
    }, [accessToken, navigate, failureRedirect]);

    const validateStep1 = () => {
        const errors = {};
        if (!isStoreData.title) {
            errors.title = Messages.ERROR.TITLE_EMPTY;
            titleInputRef.current?.focus();
        }
        setErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validatePhone = () => {
        const errors = {};
        if (!isStoreData.phone) {
            errors.phone = Messages.ERROR.EMPTY_PHONE;
            phoneInputRef.current?.focus();
        } else if (!/^\d{10}$/.test(isStoreData.phone)) {
            errors.phone = Messages.ERROR.INVALID_PHONE;
            phoneInputRef.current?.focus();
        }
        setErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let cleanedValue = value;

        if (name === "phone" || name === "verifycode") {
            cleanedValue = value.replace(/[^0-9]/g, "");
            if (name === "phone") {
                cleanedValue = cleanedValue.length > 10 ? cleanedValue.slice(-10) : cleanedValue;
            }
            if (name === "verifycode") {
                cleanedValue = cleanedValue.slice(0, 6);
            }
        }
        setStoreData({
            ...isStoreData,
            [name]: cleanedValue,
        });
        setErrors((prev) => ({ ...prev, [name]: null }));
    };

    const handleNextStep = () => {
        if (validateStep1()) {
            setCurrentStep(2);
        }
    };

    const handlePreviousStep = () => {
        setCurrentStep(1);
        setStoreData({ ...isStoreData, phone: "", verifycode: "" });
        setShowOtpInput(false);
        setPhoneVerified(false);
        setErrors({});
        setErrorMsg(null);
    };

    const handleVerifyPhone = async () => {
        setErrorMsg(null);
        if (!validatePhone()) return;

        setVerifyLoading(true);
        try {
            if (!checkNetwork()) return;
            const response = await axios.post(`${uRLs.AUTH_URL}auth-phone`, { phone: isStoreData.phone },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            setStoreData({
                ...isStoreData,
                verifyToken: response.data.results.verifyToken,
            });
            setShowOtpInput(true);
            showAlert("success", Messages.AUTH.OTP_SENT);
        } catch (error) {
            if (error.response?.data?.code === "TOKEN_EXPIRED" || error.response?.data?.code === "FORBIDDEN") {
                showAlert("warning", error.response?.data?.message);
                navigate(`/${failureRedirect}`, { replace: true, state: null });
            } else {
                setErrorMsg(error.response?.data?.message || Messages.AUTH.OTP_FAILED);
            }
        } finally {
            setVerifyLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        setErrorMsg(null);
        if (!isStoreData.verifycode) {
            setErrors({ verifycode: Messages.ERROR.EMPTY_OTP });
            verifyCodeInputRef.current?.focus();
            return;
        }
        setBtnLoading(true);
        try {
            if (!checkNetwork()) return;
            const response = await axios.post(`${uRLs.AUTH_URL}auth-otp`,
                { verifycode: isStoreData.verifycode },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${isStoreData.verifyToken}`,
                    },
                }
            );
            if (response.status === 201 && response.data.results) {
                setPhoneVerified(true);
                showAlert("success", Messages.SUCCESS.PHONE_VERIFIED);
                await handleCreate();
            }
        } catch (error) {
            if (error.response?.data?.code === "TOKEN_EXPIRED" || error.response?.data?.code === "FORBIDDEN") {
                showAlert("warning", error.response?.data?.message);
                navigate(`/${failureRedirect}`, { replace: true, state: null });
            } else {
                setErrorMsg(error.response?.data?.message || Messages.AUTH.OTP_FAILED);
            }
        } finally {
            setBtnLoading(false);
        }
    };

    const handleCreate = async () => {
        setCreateLoading(true);
        try {
            if (!checkNetwork()) return;
            const response = await axios.post(`${uRLs.API_URL}add-store`, isStoreData,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            if (response.status === 201 && response.data.results.accessToken) {
                handleLogin(response.data.results);
                showAlert("success", Messages.SUCCESS.STORE_CREATED);
                navigate(`/${successRedirect}`, { replace: true, state: null });
            }
        } catch (error) {
            if (error.response?.data?.code === "TOKEN_EXPIRED" || error.response?.data?.code === "FORBIDDEN") {
                showAlert("warning", error.response?.data?.message);
                navigate(`/${failureRedirect}`, { replace: true, state: null });
            } else {
                setErrorMsg(error.response?.data?.message || Messages.AUTH.OTP_FAILED);
            }
        } finally {
            setCreateLoading(false);
        }
    };

    const handleLink = () => {
        navigate(`/${failureRedirect}`, { replace: true, state: null });
    };

    const handleResendOTP = async () => {
        setResendBtnLoading(true);
        try {
            if (!checkNetwork()) return;
            const response = await axios.get(`${uRLs.AUTH_URL}auth-phone-resend-otp`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${isStoreData.verifyToken}`,
                },
            });
            showAlert("success", Messages.AUTH.OTP_RESENT);
        } catch (error) {
            if (error.response?.data?.code === 'TOKEN_EXPIRED') {
                showAlert("warning", error.response?.data?.message);
                navigate(`/${failureRedirect}`, { replace: true, state: null });
            } else if (error.response?.data?.code === 'FORBIDDEN') {
                showAlert("warning", error.response?.data?.message);
                navigate(`/${failureRedirect}`, { replace: true, state: null });
            } else {
                setErrorMsg(error.response?.data?.message || Messages.AUTH.OTP_FAILED);
            }
        } finally {
            setResendBtnLoading(false);
        }
    };

    return (
        <div className="wrapper">
            <div className="section-authentication-signin d-flex align-items-center justify-content-center my-5 my-lg-0">
                <div className="container">
                    <div className="row row-cols-1 row-cols-lg-2 row-cols-xl-3">
                        <div className="col mx-auto">
                            <div className="card">
                                <div className="card-header text-center auth-bg">
                                    <div className="mb-3">
                                        <img src="assets/images/logo-icon.png" width="60" alt="" />
                                    </div>
                                    <h5 className="fw-500 mb-0">Launch Your Outlet 🎉</h5>
                                    <p className="mb-0">Input your info to kick off!</p>
                                </div>
                                <div className="card-body">
                                    <ul className="nav nav-pills nav-justified form-wizard-header mb-0" role="tablist">
                                        <li className="nav-item" role="presentation">
                                            <a
                                                href="#basictab1"
                                                className={`nav-link ${currentStep === 1 ? "active" : currentStep > 1 ? "completed" : ""}`}
                                                role="tab"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (currentStep > 1) handlePreviousStep();
                                                }}
                                            >
                                                <span>{currentStep === 2 ? <CheckIcon iClass={"check-icon"}/> : "01"}</span>
                                            </a>
                                        </li>
                                        <div className={`connector ${currentStep > 1 ? "completed" : ""}`}></div>
                                        <li className="nav-item" role="presentation">
                                            <a
                                                href="#basictab2"
                                                className={`nav-link ${currentStep === 2 ? "active" : ""}`}
                                                role="tab"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (validateStep1()) setCurrentStep(2);
                                                }}
                                            >
                                                <span>02</span>
                                            </a>
                                        </li>
                                    </ul>
                                    <div className="p-4">
                                        <div className="d-block">
                                            <h6 className="fw-500 mb-0">
                                                {currentStep === 1 ? "Outlet Title" : "Verify Your Phone"} <span className="text-danger">*</span>
                                            </h6>
                                            <p className="mb-0 text-muted">
                                                {currentStep === 1 ? "Enter a title to launch." : "Enter phone number and verify the OTP."}
                                            </p>
                                        </div>
                                        <div className="form-body mt-2">
                                            <div id="basicwizard">
                                                <div className="tab-content b-0 mb-0">
                                                    <div className={`tab-pane ${currentStep === 1 ? "active show" : ""}`} id="basictab1" role="tabpanel">
                                                        <div className="row">
                                                            <div className="col-12">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    autoComplete="off"
                                                                    value={isStoreData.title}
                                                                    onChange={handleChange}
                                                                    name="title"
                                                                    ref={titleInputRef}
                                                                />
                                                                {error.title && <div className="invalid-feedback d-block">{error.title}</div>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={`tab-pane ${currentStep === 2 ? "active show" : ""}`} id="basictab2" role="tabpanel">
                                                        <div className="row g-3">
                                                            <div className="col-12">
                                                                <label htmlFor="phone" className="form-label">
                                                                    Phone <span className="text-danger">*</span>
                                                                </label>
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    <div className="input-group">
                                                                        <span className="input-group-text" id="basic-addon1">+91</span>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            autoComplete="off"
                                                                            value={isStoreData.phone}
                                                                            onChange={handleChange}
                                                                            name="phone"
                                                                            pattern="[0-9]*"
                                                                            inputMode="numeric"
                                                                            maxLength="10"
                                                                            ref={phoneInputRef}
                                                                            disabled={showOtpInput || isPhoneVerified}
                                                                        />
                                                                    </div>
                                                                    {/* {!showOtpInput && !isPhoneVerified && (
                                                                        isVerifyLoading ? (
                                                                            <button className="btn btn-success fs-10 d-flex align-items-center ms-2 pb-0" disabled aria-busy="true" aria-label="Sending OTP">
                                                                                <CircularProgress size={22} className="text-white" />
                                                                            </button>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-success fs-10 d-flex align-items-center ms-2"
                                                                                onClick={handleVerifyPhone}
                                                                                disabled={isStoreData.phone.length !== 10}
                                                                                aria-label="Send OTP"
                                                                            >
                                                                                Send OTP
                                                                            </button>
                                                                        )
                                                                    )} */}
                                                                </div>
                                                                {error.phone && <div className="invalid-feedback d-block">{error.phone}</div>}
                                                            </div>
                                                            {showOtpInput && !isPhoneVerified && (
                                                                <div className="col-12 mt-3">
                                                                    <label htmlFor="verifycode" className="form-label">
                                                                        OTP <span className="text-danger">*</span>
                                                                    </label>
                                                                    <label htmlFor="verifycode" className="form-label text-right">
                                                                        {isResendBtnLoading ? (
                                                                            <button className="btn btn-sm bg-label-send shadow-none pb-0 pt-1" disabled>
                                                                                <CircularProgress size={18} className="text-white" />
                                                                            </button>
                                                                        ) : (
                                                                            <button type="button" onClick={handleResendOTP} className="btn btn-sm bg-label-send shadow-none">
                                                                                Resend OTP
                                                                            </button>
                                                                        )}
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        autoComplete="off"
                                                                        value={isStoreData.verifycode}
                                                                        onChange={handleChange}
                                                                        name="verifycode"
                                                                        pattern="[0-9]*"
                                                                        inputMode="numeric"
                                                                        maxLength="6"
                                                                        ref={verifyCodeInputRef}
                                                                    />
                                                                    {/* <div className="d-flex align-items-center justify-content-between">
                                                                        {isBtnLoading || isCreateLoading ? (
                                                                            <button className="btn btn-success fs-10 d-flex align-items-center ms-2 pb-0" disabled aria-busy="true" aria-label="Verifying OTP">
                                                                                <CircularProgress size={22} className="text-white" />
                                                                            </button>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-success fs-10 d-flex align-items-center ms-2"
                                                                                onClick={handleVerifyOTP}
                                                                                disabled={isStoreData.verifycode.length !== 6}
                                                                                aria-label="Verify OTP"
                                                                            >
                                                                                Verify OTP
                                                                            </button>
                                                                        )}
                                                                    </div> */}
                                                                    {error.verifycode && <div className="invalid-feedback d-block">{error.verifycode}</div>}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                {errorMsg && <div className="invalid-feedback d-block text-center">{errorMsg}</div>}
                                                <div className="wizard-buttons mt-3">
                                                    {currentStep === 2 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary"
                                                            onClick={handlePreviousStep}
                                                            disabled={isVerifyLoading || isBtnLoading || isCreateLoading}
                                                            aria-label="Go back"
                                                        >
                                                            Back
                                                        </button>
                                                    )}
                                                    {currentStep === 1 ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={handleNextStep}
                                                            disabled={isStoreData.title.length === 0}
                                                            aria-label="Next"
                                                        >
                                                            Next
                                                        </button>
                                                    ) : !showOtpInput ? (
                                                        isVerifyLoading ? (
                                                            <button className="btn btn-primary pb-0" disabled aria-busy="true" aria-label="Sending OTP">
                                                                <CircularProgress size={22} className="text-white" />
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                onClick={handleVerifyPhone}
                                                                disabled={isStoreData.phone.length !== 10}
                                                                aria-label="Send OTP"
                                                            >
                                                                Send OTP
                                                            </button>
                                                        )
                                                    ) : isBtnLoading || isCreateLoading ? (
                                                        <button className="btn btn-primary pb-0" disabled aria-busy="true" aria-label="Verifying OTP">
                                                            <CircularProgress size={22} className="text-white" />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={handleVerifyOTP}
                                                            disabled={isStoreData.verifycode.length !== 6}
                                                            aria-label="Verify and Launch"
                                                        >
                                                            Verify & Launch
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-12 mt-3">
                                                <div className="text-center">
                                                    <Link
                                                        to={`/${failureRedirect}`}
                                                        className="d-flex align-items-center justify-content-center text-decoration-none"
                                                        onClick={handleLink}
                                                        style={{ pointerEvents: isVerifyLoading || isBtnLoading || isCreateLoading ? "none" : "auto" }}
                                                        aria-disabled={isVerifyLoading || isBtnLoading || isCreateLoading}
                                                        aria-label="Logout"
                                                    >
                                                        <ChevronLeftIcon iClass={"me-1 fs-18"}/>
                                                        Logout
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthStore;